import React from 'react';
import ReactDOM from 'react-dom/client';
import { store } from '@store';
import axios from 'axios';
import { Auth } from 'aws-amplify';
import { InitializeAmplify, setAdditionalProperties, initializeFullStory } from 'fmc-digital-common';
import TagManager from 'react-gtm-module';
import { auth, config } from './config';
import App from './App';
import { injectStore } from '@utils/fetch';
import { v4 } from 'uuid';
import { logUserEvent } from '@analytics/amplifyLogging';
import 'styles.css';
import './fonts/CanelaText-Black-App.ttf';
import './fonts/CanelaText-Black-Web.woff';
import './fonts/CanelaText-Black-Web.woff2';
import './fonts/Poppins-Medium.ttf';
import './fonts/Poppins-Regular.ttf';
import './fonts/Poppins-SemiBold.ttf';
import { HashRouter } from 'react-router-dom';
import { OktaAuth } from '@okta/okta-auth-js';
import { get } from 'lodash';
import './i18n';
import { initI18Next } from './i18n';
const baseUrl = window.location.origin;
axios
    .get(`${baseUrl}/config.json`)
    .then(async response => {
        initI18Next(response.data.environment).then(console.log);
        await config.set(response.data);
        initializeFullStory({ orgId: 'WYV65' });
        TagManager.initialize({
            gtmId: config.env('gtmId')
        });

        /*
         * Dynatrace Integration
         * environment = 'test' | 'uat' | 'prod'
         * if environment is empty or is prod -> initialize with prod config url
         * otherwise -> initialize with non-prod config url
         * */
        const script = document.createElement('script');
        const environment = get(response, ['data', 'environment'], undefined);

        script.id = 'customer-portal-ui';
        script.src =
            !environment || environment === 'prod'
                ? 'https://js-cdn.dynatrace.com/jstag/157944990f8/bf38474zpy/27041b0c17cdf826_complete.js'
                : `https://js-cdn.dynatrace.com/jstag/15c157a40ab/bf57393wrn/9e3b7c29b7aa985_complete.js`;
        script.type = 'text/javascript';
        script.crossOrigin = 'anonymous';
        document.body.appendChild(script);

        try {
            InitializeAmplify({
                aws_project_region: config.env('awsProjectRegion'),
                aws_cognito_identity_pool_id: config.env('awsCognitoIdentityPoolId'),
                aws_cognito_region: config.env('awsCognitoRegion'),
                aws_mobile_analytics_app_id: config.env('awsMobileAnalyticsAppId'),
                aws_mobile_analytics_app_region: config.env('awsMobileAnalyticsAppRegion')
            });
            setAdditionalProperties('amplify', { application: 'AuthCustomerSrvApp' });
            setAdditionalProperties('xAPIKey', config.env('xApiKey'));
            setAdditionalProperties('selfServUrl', config.env('selfServUrl'));
            logUserEvent({
                type: 'ACP Initialized',
                description: 'Amplify configuration for ACP application was successful',
                action: 'AmplifyConfigWasSuccessful',
                uuid: v4()
            });
        } catch (e) {
            logUserEvent({
                type: 'ACP Initialized',
                description: `Amplify configuration for ACP application was failed. ${JSON.stringify(e)}`,
                action: 'AmplifyConfigFailed',
                uuid: v4()
            });
        }
        try {
            Auth.configure({ mandatorySignIn: false });
            logUserEvent({
                type: 'ACP Initialized',
                action: 'AuthConfigurationForACPWaSuccessful.',
                description: 'Auth configuration for ACP application was successful.',
                uuid: v4()
            });
        } catch (e) {
            logUserEvent({
                type: 'ACP Initialized',
                description: `Auth configuration for ACP application was failed. ${JSON.stringify(e)}`,
                action: 'AuthConfigurationACPWasFailed',
                uuid: v4()
            });
        }
        const instance = new OktaAuth({
            clientId: config.env('clientId'),
            redirectUri: config.env('redirectUri') + '/callback',
            issuer: config.env('issuer'),
            postLogoutRedirectUri: config.env('postLogoutRedirectUri'),
            scopes: [
                'openid',
                'profile',
                'web-unauth',
                'edm.api.list',
                'edm.api.upload',
                'edm.api.view',
                'edm.api.get',
                config.env('environment') === 'prod' ? 'customerweb' : `customerweb-${config.env('environment')}`
            ],
            tokenManager: { storage: 'sessionStorage' },
            maxClockSkew: Number(config.env('maxClockSkew'))
        });
        auth.setOkta(instance);
        logUserEvent({
            type: 'ACP Initialized',
            description: 'Okta Auth configuration setting for ACP application was successful.',
            action: 'OktaAuthConfigurationACPWaSuccessful',
            uuid: v4()
        });
    })
    .catch(err => {
        console.log('Error occurred in before application initialization new', err);
        logUserEvent({
            type: 'ACP Initialized',
            description: `Error Occurred before application loading ${JSON.stringify(err)}`,
            action: 'OktaAuthConfigurationACPFailed',
            uuid: v4()
        });
    })
    .finally(() => {
        injectStore(store);
        const root = ReactDOM.createRoot(document.getElementById('root'));
        root.render(
            <React.StrictMode>
                <HashRouter>
                    <App />
                </HashRouter>
            </React.StrictMode>,
            root
        );
    });
